import _toLower from 'lodash/toLower';
import _startCase from 'lodash/startCase';
import { pad, scale } from '@cloudinary/url-gen/actions/resize';
import { center } from '@cloudinary/url-gen/qualifiers/compass';
import { compass } from '@cloudinary/url-gen/qualifiers/gravity';
import formatcoords from 'formatcoords';
import pluralize from 'pluralize';
import { imageFromUrl } from 'src/services/cloudinary';
import { dayjs } from 'src/services/date';
import { convertFuelEconomy, convertVelocity, getFuelEconomyUnits, getVelocityUnits } from 'src/services/locale';
import { convertDistance, getDistanceUnits } from 'src/services/locale/index';

export class VueFilters {
  /**
   * Capitalizes the given value.
   *
   * @param {any} value
   * @returns {String}
   */
  static capitalize(value) {
    if (!value) {
      return '';
    }
    const str = value.toString();
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Returns a string with a language-sensitive representation of the given number.
   *
   * @param {Number} value
   * @returns {String}
   */
  static localeString(value) {
    return Number(value).toLocaleString();
  }

  /**
   * Adds a comma after the thousands place if given number is >= 1,000.
   *
   * @param {any} value
   * @returns {String} Number (as string) with comma or the original value
   */
  static thousandsComma(value) {
    const intValue = parseInt(value, 10);

    if (Number.isNaN(intValue) || intValue < 1000) {
      return value;
    }

    const valueString = value.toString();
    const decimalSplit = valueString.split('.');
    const integer = decimalSplit[0];
    const decimal = decimalSplit[1] ? `.${decimalSplit[1]}` : '';

    const lastThreeDigits = integer.length - 3;

    const thousandsComma = `${integer.substring(0, lastThreeDigits)},${integer.substring(lastThreeDigits)}`;

    return thousandsComma + decimal;
  }

  /**
   * Converts the given seconds to an hours and minutes phrase.
   *
   * @param {Number} seconds
   * @returns {String} x_hr(s) x_min(s)
   */
  static secondsToHoursMinutes(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds - hours * 3600) / 60);

    const hoursAndMins = [];

    if (hours > 0) {
      hoursAndMins.push(`${hours}${getLabel(hours, 'hr')}`);
    }

    if (minutes > 0) {
      hoursAndMins.push(`${minutes}${getLabel(minutes, 'min')}`);
    }

    if (hours === 0 && minutes === 0) {
      const intSeconds = Math.floor(seconds);
      hoursAndMins.push(`${intSeconds}${getLabel(intSeconds, 'second')}`);
    }

    if (hours === 0 && minutes === 0 && seconds === 0) {
      return '0 seconds';
    }

    return hoursAndMins.join(' ');

    function getLabel(value, label) {
      if (value > 1) {
        return ` ${label}s`;
      }
      if (value === 1) {
        return ` ${label}`;
      }
      return '';
    }
  }

  /**
   * Converts the given seconds to a days/hours/minutes phrase.
   *
   * @param {Number} seconds
   * @returns {String} x_day(s) x_hr(s) x_min(s)
   */
  static secondsToDaysHoursMinutes(seconds) {
    const SECONDS_PER_MINUTE = 60;
    const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;
    const SECONDS_PER_DAY = SECONDS_PER_HOUR * 24;
    const days = Math.floor(seconds / SECONDS_PER_DAY);
    const hours = Math.floor((seconds - days * SECONDS_PER_DAY) / SECONDS_PER_HOUR);
    const minutes = Math.floor((seconds - (hours * SECONDS_PER_HOUR + days * SECONDS_PER_DAY)) / SECONDS_PER_MINUTE);

    const daysHoursMins = [];

    if (days > 0) {
      daysHoursMins.push(`${days}${getLabel(days, 'day')}`);
    }

    if (hours > 0) {
      daysHoursMins.push(`${hours}${getLabel(hours, 'hr')}`);
    }

    if (minutes > 0) {
      daysHoursMins.push(`${minutes}${getLabel(minutes, 'min')}`);
    }

    return daysHoursMins.join(' ');

    function getLabel(value, label) {
      if (value > 1) {
        return ` ${label}s`;
      }
      if (value === 1) {
        return ` ${label}`;
      }
      return '';
    }
  }

  /**
   * Converts the given seconds to a days/hours/minutes phrase.
   *
   * @param {String} timestamp1
   * @param {String} timestamp2
   * @returns {String} x_day(s) x_hr(s) x_min(s)
   */
  static timestampsToDaysHoursMinutes(ts1, ts2) {
    const start = dayjs(ts1);
    const end = dayjs(ts2);
    const durationInSeconds = end.diff(start, 'second');
    return VueFilters.secondsToDaysHoursMinutes(durationInSeconds);
  }

  /**
   * Converts a given number to a fixed decimal (string) or '--' for non-finite inputs
   *
   * @param {Number} num
   * @param {Number} places
   * @returns {String}
   */
  static toDecimalPlaces(num, places = 0) {
    if (Number.isFinite(num)) {
      return num.toFixed(places);
    }
    return '--';
  }

  /**
   * Turns 1.200000001 into 1.2
   *
   * @param {Number} num
   * @returns {Number}
   */
  static dropRightZeros(num) {
    // don't mess with whole numbers
    if (Number.isInteger(num)) {
      return num;
    }
    return parseFloat(num.toFixed(5));
  }

  /**
   * Take a distance in miles/km as input
   * Return in feet/meters for distances less than a 0.1 miles/km; else in miles/km
   * TODO: write tests for this
   */
  static humanizedDistanceUnits(num) {
    const trimmedValue = VueFilters.dropRightZeros(num);
    const largeUnit = getDistanceUnits();
    const smallUnit = largeUnit === 'km' ? 'm' : 'ft';
    const isMetric = largeUnit === 'km';
    if (trimmedValue >= 0.1) {
      return `${trimmedValue} ${largeUnit}`;
    }
    if (trimmedValue) {
      const smallUnitValue = isMetric ? getMeters(trimmedValue) : getFeet(trimmedValue);
      return `${smallUnitValue} ${smallUnit}`;
    }
    return '--';

    function getFeet(miles) {
      return Math.round(miles * 5280);
    }

    function getMeters(km) {
      return Math.round(km * 1000);
    }
  }

  /**
   * Formats a given date string to M/D/YYYY format.
   *
   * @static
   * @param {String} dateString
   * @returns {String}
   */
  static monthDayYear(dateString) {
    return dayjs(dateString).format('M/D/YYYY');
  }

  static utcToLocalDate(utcTimestamp) {
    let dateString;

    if (!utcTimestamp) {
      return utcTimestamp;
    }

    const ts = dayjs.utc(utcTimestamp).local();

    const today = dayjs();

    if (ts.isSame(today, 'day')) {
      dateString = 'Today';
    } else if (today.subtract(1, 'day').isSame(ts, 'day')) {
      dateString = 'Yesterday';
    } else {
      dateString = ts.format('M/D/YY');
    }

    return `${dateString}`;
  }

  static utcToLocalDateTime(utcTimestamp) {
    let dateString;

    if (!utcTimestamp) {
      return utcTimestamp;
    }

    const ts =
      typeof utcTimestamp === 'string' || typeof utcTimestamp === 'number'
        ? dayjs.utc(utcTimestamp).local()
        : utcTimestamp;

    const today = dayjs();

    if (ts.isSame(today, 'day')) {
      dateString = 'Today';
    } else if (today.subtract(1, 'day').isSame(ts, 'day')) {
      dateString = 'Yesterday';
    } else {
      dateString = ts.format('M/D/YY');
    }

    return `${dateString} ${ts.format('h:mm A')}`;
  }

  /**
   * Converts a given UTC time into a local time "h:mm A" format.
   *
   * @param {String} timeString
   * @returns {String}
   */
  static utcToLocalTimestamp(timeString) {
    return dayjs.utc(timeString).local().format('h:mm A');
  }

  static fullDistanceUOM(shortUOM) {
    let uom;
    switch (shortUOM) {
      case 'mi':
        uom = 'miles';
        break;
      case 'km':
        uom = 'kilometers';
        break;
      default:
        uom = shortUOM;
    }
    return uom;
  }

  /**
   * Pads/fits the given image URL to the given height/width.
   *
   * @param {String} url
   * @param {Object} size
   * @param {Number} size.height
   * @param {Number} size.width
   * @returns {String}
   */
  static cloudinaryFit(url, { height, width }) {
    const image = imageFromUrl(url);
    image.resize(pad(width, height).gravity(compass(center())));
    return image.toURL();
  }

  /**
   * Scales the given image URL to the given height/width.
   *
   * @param {String} url
   * @param {Object} size
   * @param {Number} size.height
   * @param {Number} size.width
   * @returns {String}
   */
  static cloudinaryScale(url, { height, width }) {
    const image = imageFromUrl(url);

    const imageScale = scale();
    if (height) {
      imageScale.height(height);
    }
    if (width) {
      imageScale.width(width);
    }

    image.resize(imageScale);
    return image.toURL();
  }

  static pluralize(number, phrase) {
    return pluralize(phrase, number);
  }

  static addListSeparator(text, index, length, separator = ',') {
    const comma = index < length - 1 ? `${separator} ` : '';
    return `${text}${comma}`;
  }

  static startCase(value) {
    return _startCase(_toLower(value));
  }

  /**
   * Converts the given value to lowercase.
   *
   * @param {String} value
   * @returns {String}
   */
  static lowercase(value) {
    return _toLower(value);
  }

  /**
   * Converts the given value to uppercase.
   *
   * @param {String} value
   * @returns {String}
   */
  static uppercase(value) {
    return String(value).toUpperCase();
  }

  /**
   * Converts the given meters to miles.
   *
   * @param {Number} meters
   * @returns {Number}
   */
  static metersToMiles(meters) {
    return meters * 0.000621371192;
  }

  /**
   * Converts the given miles to km.
   *
   * @param {Number} miles
   * @returns {Number}
   */
  static milesToKm(miles = 0) {
    return miles * 1.60934;
  }

  static fromNow(time) {
    return dayjs(time).fromNow();
  }

  /**
   * Converts the given coordintes ([long, lat]) to a degrees, minutes, and seconds (DMS) string.
   *
   * @param {Number[]} coordinates
   * @returns {String}
   */
  static dmsCoordinates(coordinates) {
    if (!coordinates) {
      return '';
    }

    // Assume coordinates are in long,lat format and reverse it
    try {
      const coordsObj = formatcoords(...[...coordinates].reverse());
      return coordsObj.format();
    } catch (error) {
      return '';
    }
  }

  /**
   * Converts degrees fahrenheit to celsius
   *
   * @param {Number} degrees
   * @returns {Number}
   */
  static fahreheitToCelsius(degreesF) {
    return ((degreesF - 32) * 5) / 9;
  }

  /**
   * Converts degrees celsius to fahrenheit
   *
   * @param {Number} degrees
   * @returns {Number}
   */
  static celsiusToFahreheit(degreesC) {
    return (degreesC * 9) / 5 + 32;
  }

  /**
   * Converts the given fuel economy to the appropriate value + units based on the current locale.
   *
   * @param {Number} value
   * @param {String} fromUnit
   * @returns {String}
   */
  static fuelEconomyLocalized(economy, fromUnit) {
    const roundedEconomy = convertFuelEconomy(economy, fromUnit);
    return roundedEconomy ? `${roundedEconomy.toFixed(1)} ${getFuelEconomyUnits()}` : '';
  }

  /**
   * Converts the given distance to the distance appropriate for the given locale.
   *
   * @param {Number} distance
   * @param {String} fromUnit
   * @param {Boolean} long
   * @returns {String}
   */
  static distanceLocalized(distance, fromUnit, long = false) {
    const roundedDistance = Math.round(convertDistance(distance, fromUnit));
    return roundedDistance
      ? `${VueFilters.numberFormat(roundedDistance)} ${getDistanceUnits(roundedDistance, long)}`
      : '';
  }

  /**
   * Converts the given velocity to the appropriate value + units based on the current locale.
   *
   * @param {Number} velocity
   * @param {String} fromUnit
   * @returns {String}
   */
  static speedLocalized(velocity, fromUnit) {
    const roundedVelocity = Math.round(convertVelocity(velocity, fromUnit));
    return roundedVelocity ? `${roundedVelocity} ${getVelocityUnits()}` : '';
  }

  /**
   * Formats the given number based on Intl.NumberFormat.
   *
   * @param {Number} value
   * @returns {String}
   */
  static numberFormat(value) {
    const numberFormat = new Intl.NumberFormat();
    return numberFormat.format(value);
  }
}

export default ({ Vue }) => {
  // Note: using "exports" for testability
  Vue.filter('motionStatus', (value) => VueFilters.motionStatus(value, Vue));
  Vue.filter('capitalize', VueFilters.capitalize);
  Vue.filter('thousandsComma', VueFilters.thousandsComma);
  Vue.filter('secondsToHoursMinutes', VueFilters.secondsToHoursMinutes);
  Vue.filter('secondsToDaysHoursMinutes', VueFilters.secondsToDaysHoursMinutes);
  Vue.filter('timestampsToDaysHoursMinutes', VueFilters.timestampsToDaysHoursMinutes);
  Vue.filter('toDecimalPlaces', VueFilters.toDecimalPlaces);
  Vue.filter('dropRightZeros', VueFilters.dropRightZeros);
  Vue.filter('humanizedDistanceUnits', VueFilters.humanizedDistanceUnits);
  Vue.filter('utcToLocalDate', VueFilters.utcToLocalDate);
  Vue.filter('utcToLocalDateTime', VueFilters.utcToLocalDateTime);
  Vue.filter('utcToLocalTimestamp', VueFilters.utcToLocalTimestamp);
  Vue.filter('fullDistanceUOM', VueFilters.fullDistanceUOM);
  Vue.filter('cloudinaryFit', VueFilters.cloudinaryFit);
  Vue.filter('cloudinaryScale', VueFilters.cloudinaryScale);
  Vue.filter('pluralize', VueFilters.pluralize);
  Vue.filter('monthDayYear', VueFilters.monthDayYear);
  Vue.filter('addListSeparator', VueFilters.addListSeparator);
  Vue.filter('startCase', VueFilters.startCase);
  Vue.filter('uppercase', VueFilters.uppercase);
  Vue.filter('localeString', VueFilters.localeString);
  Vue.filter('metersToMiles', VueFilters.metersToMiles);
  Vue.filter('milesToKm', VueFilters.milesToKm);
  Vue.filter('fromNow', VueFilters.fromNow);
  Vue.filter('dmsCoordinates', VueFilters.dmsCoordinates);
  Vue.filter('lowercase', VueFilters.lowercase);
  Vue.filter('fahreheitToCelsius', VueFilters.fahreheitToCelsius);
  Vue.filter('celsiusToFahreheit', VueFilters.celsiusToFahreheit);
  Vue.filter('speedLocalized', VueFilters.speedLocalized);
  Vue.filter('fuelEconomyLocalized', VueFilters.fuelEconomyLocalized);
  Vue.filter('distanceLocalized', VueFilters.distanceLocalized);
  Vue.filter('numberFormat', VueFilters.numberFormat);
};
